<template>
    <div class="image-hotspots">
        <div class="image-frame"
             @mousedown="startDrag"
             @mousemove="whileDragging"
             @mouseup="endDrag"
             @mouseleave="endDrag"

             :class="{'show-content': activeSpot}"
        >
            <img :src="data.image.content" />
            <div v-if="dragging" :style="selectionStyle" class="selection-box"></div>

            <div
                class="image-spot"
                v-for="spot in data.spots"
                :style="{
                    left: spot.attributes.x,
                    top: spot.attributes.y,
                    width: spot.attributes.width,
                    height: spot.attributes.height
                }"
                :class="{'bordered': spot.attributes.border}"
                @click="activeSpot = spot"
            >
                <div v-if="spot.attributes.icon" class="icon">
                    <i class="fas fa-plus-circle"></i>
                </div>
            </div>

            <div
                v-if="activeSpot"
                class="image-spot-content"
            >
                <div class="spot-tools">
                    <div class="columns">
                        <div class="column">
                            <i class="fad fa-pencil mr-2 ml-1"/>Edit Hotspot
                        </div>
                        <div class="column is-narrow">
                            <b-switch v-model="activeSpot.attributes.icon">Show Icon</b-switch>
                            <b-switch v-model="activeSpot.attributes.border">Show Border</b-switch>
                        </div>
                        <div class="column is-narrow">
                            <span class="tool" @click="removeSpot"><i class="fas fa-trash-alt"></i></span>
                            <span class="tool" @click="activeSpot=null"><i class="fas fa-times"></i></span>
                        </div>
                    </div>

                </div>

                <editor-js
                    :data="activeSpot.content"
                    @save="(d) => save(d)"
                />
            </div>
        </div>

        <div class="image-tools pt-0 pl-4 pr-4 pb-4">
            <div class="pl-4" v-if="data.image.content">
                <i class="fas fa-info-circle mr-2"></i>Click and drag on image to create a hotspot. Click hotspot to edit.
            </div>
            <hr/>
            <div class="pl-4 mb-3">
                <b-button @click="mediaPicker=true"><i class="far fa-image mr-2"></i>Choose Photo</b-button>
            </div>
        </div>

        <media-picker class="library" type="PHOTO" @choose="chooseMedia" @close="mediaPicker=false" v-if="mediaPicker"></media-picker>
    </div>
</template>

<script>
    import MediaPicker from "../../../cards/media_library/MediaPicker.vue";
    import EditorJs from "../EditorJs.vue";

    export default {
        props: ['data'],
        components: {EditorJs, MediaPicker},
        data: function () {
            return {
                mediaPicker: false,

                dragging: false,
                dragStartX: null,
                dragStartY: null,
                dragEndX: null,
                dragEndY: null,

                activeSpot: null
            };
        },
        computed: {
            selectionStyle() {
                const left = Math.min(this.dragStartX, this.dragEndX);
                const top = Math.min(this.dragStartY, this.dragEndY);
                const width = Math.abs(this.dragStartX - this.dragEndX);
                const height = Math.abs(this.dragStartY - this.dragEndY);
                return {
                    left: `${left}%`,
                    top: `${top}%`,
                    width: `${width}%`,
                    height: `${height}%`
                };
            }
        },
        methods: {
            startDrag(event) {
                if (!this.activeSpot && event.target.tagName === 'IMG') {
                    event.preventDefault();
                    this.dragging = true;
                    this.boundingRect = event.target.getBoundingClientRect(); // Store it once here
                    this.dragStartX = ((event.clientX - this.boundingRect.left) / this.boundingRect.width) * 100;
                    this.dragStartY = ((event.clientY - this.boundingRect.top) / this.boundingRect.height) * 100;
                    this.dragEndX = this.dragStartX;
                    this.dragEndY = this.dragStartY;
                }
            },

            whileDragging(event) {
                if (this.dragging) {
                    this.dragEndX = ((event.clientX - this.boundingRect.left) / this.boundingRect.width) * 100;
                    this.dragEndY = ((event.clientY - this.boundingRect.top) / this.boundingRect.height) * 100;
                }
            },
            endDrag() {
                if (this.dragging) {
                    this.dragging = false;

                    let width = Math.abs(this.dragStartX - this.dragEndX);
                    let height = Math.abs(this.dragStartY - this.dragEndY);

                    if (width > 4 && height > 4) {
                        this.data.spots.push({
                            content: {},
                            attributes: {
                                x: `${Math.min(this.dragStartX, this.dragEndX)}%`,
                                y: `${Math.min(this.dragStartY, this.dragEndY)}%`,
                                width: `${width}%`,
                                height: `${height}%`,
                                icon: false,
                                border: false
                            }
                        });
                    } else {
                        window.alert('Hotspot not created. Please select a larger area.');
                    }
                }
            },
            removeSpot() {
                if (confirm('Are you sure you want to delete this hotspot?')) {
                    this.data.spots.splice(this.data.spots.indexOf(this.activeSpot), 1);
                    this.activeSpot = null;
                }
            },
            save(data) {
                this.activeSpot.content = data;
            },
            chooseMedia: function (media) {
                this.mediaPicker = false;
                this.data.image.content = media.url;
            }
        },
        created() {
            if (!this.data.spots) {
                this.data.image = {content: null, attributes: {}};
                this.data.spots = [];
            }
        }
    };
</script>

<style lang="scss" scoped>

    .image-hotspots {
        .image-frame {
            margin: 10px 2rem 2rem;
            position: relative;
            cursor: crosshair;

            img {
                transition: filter 0.3s ease; /* Smooth transition for the blur effect */
                display: block;
            }

            .selection-box {
                position: absolute;
                border: 2px dashed #009900;
                border-radius: 5px;
                background-color: rgba(4, 244, 26, 0.3);
            }

            .image-spot {
                position: absolute;
                border: 2px dashed #336699;
                border-radius: 5px;
                cursor: pointer;

                &.bordered {
                    border: 2px solid #336699;
                }

                .icon {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: rgba(255,255, 255, 0.8);
                    font-size: 3.5rem;
                }
            }

            .image-spot-content {
                position: absolute;
                background-color: rgba(255, 255, 255, 0.85);
                display: none;
                left: 12px;
                right: 12px;
                top: 12px;
                bottom: 8px;
                overflow-x: hidden;
                overflow-y: auto;
                border-radius: 5px;

                .spot-tools {
                    background: white;
                    margin-bottom: 1rem;
                    padding: 0.5rem;
                    border-bottom: 1px solid #ccc;
                    font-size: 0.9rem;
                    font-weight: bold;
                    color: #676767;

                    span.tool {
                        padding: 10px;
                        font-size: 1rem;
                        color: #676767;

                        &:hover {
                            cursor: pointer;
                            color: #222222;
                        }
                    }
                }
            }

            &.show-content {
                cursor: default;
                img {
                    filter: blur(8px);
                }

                .image-spot {
                    display: none;
                }

                .image-spot-content {
                    display: block;
                }
            }

        }


    }

</style>
